import { useTranslation } from 'react-i18next';

export const BPIngredients = () => {
  const { t } = useTranslation();

  const ballsFeatures = [
    {
      icon: 'ri-drop-fill',
      title: t('bp_ing_balls_f1_title'),
      desc: t('bp_ing_balls_f1_desc'),
    },
    {
      icon: 'ri-goblet-line',
      title: t('bp_ing_balls_f2_title'),
      desc: t('bp_ing_balls_f2_desc'),
    },
    {
      icon: 'ri-bubble-chart-line',
      title: t('bp_ing_balls_f3_title'),
      desc: t('bp_ing_balls_f3_desc'),
    },
    {
      icon: 'ri-leaf-line',
      title: t('bp_ing_balls_f4_title'),
      desc: t('bp_ing_balls_f4_desc'),
    },
  ];

  return (
    <section id="ingredienten" className="w-full py-20 bg-gradient-to-b from-[#060012] to-[#050010]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">{t('bp_ing_badge')}</p>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
            {t('bp_ing_title').split(' ').slice(0, -1).join(' ')}{' '}
            <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">
              {t('bp_ing_title').split(' ').slice(-1)[0]}
            </span>
          </h2>
          <p className="text-white/45 text-sm max-w-xl mx-auto leading-relaxed">
            {t('bp_ing_sub')}
          </p>
        </div>

        {/* Purity Banner */}
        <div className="relative rounded-2xl overflow-hidden border border-violet-500/15 mb-12">
          <img
            src="https://public.readdy.ai/ai/img_res/1e4b30fda7273a6bfa2ef628ff99a42c.jpg"
            alt="Blue Protect ingrediënten"
            className="w-full h-48 lg:h-64 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050010]/95 via-[#050010]/70 to-[#050010]/40" />
          <div className="absolute inset-0 flex items-center px-8 lg:px-12">
            <div>
              <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-2">{t('bp_ing_promise_badge')}</p>
              <h3 className="text-white font-black text-xl lg:text-2xl mb-3">
                {t('bp_ing_promise_title').split('.')[0]}.<br />
                <span className="text-violet-300">{t('bp_ing_promise_title').split('.').slice(1).join('.').trim()}</span>
              </h3>
              <p className="text-white/50 text-sm max-w-lg leading-relaxed">
                {t('bp_ing_promise_desc')}
              </p>
            </div>
          </div>
        </div>

        {/* Ingredient Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-[#050010] to-[#0a0820] border border-violet-500/25 rounded-2xl p-8 transition-all duration-300">
            <div className="flex items-start gap-5 mb-5">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl border border-violet-500/25 bg-black/30 shrink-0">
                <i className="ri-drop-line text-violet-300 text-2xl" />
              </div>
              <div>
                <h4 className="text-white font-bold text-lg">{t('bp_ing_name')}</h4>
                <p className="text-white/30 text-sm italic mt-0.5">{t('bp_ing_latin')}</p>
              </div>
            </div>

            <p className="text-white/60 text-sm leading-relaxed mb-5">{t('bp_ing_desc')}</p>

            <div className="flex items-start gap-2 mb-6">
              <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-map-pin-2-line text-white/25 text-xs" />
              </div>
              <p className="text-white/35 text-sm leading-relaxed">{t('bp_ing_origin')}</p>
            </div>

            <div>
              <p className="text-white/25 text-xs mb-3 uppercase tracking-wider">{t('bp_ing_found_in')}</p>
              <div className="flex flex-wrap gap-2">
                {['Soap', 'Balls', 'Powder', 'Spray'].map(prod => (
                  <span
                    key={prod}
                    className="px-3 py-1 text-sm rounded-full border border-violet-500/25 bg-black/20 text-violet-300 font-medium"
                  >
                    {prod}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Balls Unique Feature Block */}
        <div className="mt-14">
          <div className="text-center mb-10">
            <p className="text-fuchsia-400 text-xs font-semibold uppercase tracking-widest mb-3">{t('bp_ing_balls_badge')}</p>
            <h3 className="text-2xl lg:text-3xl font-black text-white mb-3">
              {t('bp_ing_balls_title').split('—')[0].trim()} —{' '}
              <span className="text-transparent bg-gradient-to-r from-fuchsia-400 to-violet-400 bg-clip-text">
                {t('bp_ing_balls_title').split('—')[1]?.trim()}
              </span>
            </h3>
            <p className="text-white/40 text-sm max-w-lg mx-auto leading-relaxed">
              {t('bp_ing_balls_sub')}
            </p>
          </div>

          {/* Feature grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {ballsFeatures.map((feat) => (
              <div
                key={feat.title}
                className="bg-gradient-to-br from-[#0a0820] to-[#070015] border border-fuchsia-500/15 rounded-2xl p-6 hover:border-fuchsia-500/35 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-fuchsia-600/15 border border-fuchsia-500/20 mb-4">
                  <i className={`${feat.icon} text-fuchsia-400 text-lg`} />
                </div>
                <h4 className="text-white font-bold text-sm mb-2 leading-snug">{feat.title}</h4>
                <p className="text-white/45 text-xs leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>

          {/* Visual how-to strip */}
          <div className="relative rounded-2xl overflow-hidden border border-fuchsia-500/15">
            <img
              src="https://readdy.ai/api/search-image?query=blue%20indigo%20bath%20bomb%20dissolving%20in%20clear%20water%20glass%20bowl%20deep%20royal%20blue%20color%20spreading%20through%20water%20mystical%20spiritual%20cleansing%20ritual%20dark%20elegant%20background%20blue%20pigment%20dissolving%20swirling%20water%20premium%20product%20photography%20dark%20stone%20surface&width=1400&height=320&seq=bpballs-dissolve&orientation=landscape"
              alt="Blue Protect Ball oplossend in water"
              className="w-full h-44 lg:h-56 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050010]/95 via-[#050010]/75 to-[#050010]/30" />
            <div className="absolute inset-0 flex items-center px-8 lg:px-14">
              <div className="max-w-xl">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-information-line text-fuchsia-400 text-sm" />
                  </div>
                  <p className="text-fuchsia-400 text-xs font-semibold uppercase tracking-widest">{t('bp_ing_how_badge')}</p>
                </div>
                <p className="text-white font-bold text-base lg:text-lg leading-snug mb-3">
                  {t('bp_ing_how_title')}
                </p>
                <div className="flex flex-wrap gap-4">
                  {[t('bp_ing_how_f1'), t('bp_ing_how_f2'), t('bp_ing_how_f3')].map((feat) => (
                    <div key={feat} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-checkbox-circle-fill text-fuchsia-400 text-sm" />
                      </div>
                      <span className="text-white/55 text-xs">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
